'use client';

import { Button } from '@zealot/ui/components/button';
import { HStack } from '@zealot/ui/components/hstack';
import { SimpleSelect } from '@zealot/ui/components/simple-select';
import { cn } from '@zealot/ui/lib/utils';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import type * as React from 'react';

type PaginationProps = {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  total?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  summary?: React.ReactNode;
  className?: string;
};

const DEFAULT_PAGE_SIZES = [10, 20, 30, 50];

const getPageRange = (page: number, pageSize: number, total: number) => ({
  start: total === 0 ? 0 : (page - 1) * pageSize + 1,
  end: Math.min(page * pageSize, total),
});

// gloss
const Pagination = ({
  page,
  pageCount,
  onPageChange,
  total,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZES,
  onPageSizeChange,
  summary,
  className,
}: PaginationProps) => {
  const isPageCountKnown = pageCount >= 0;
  const lastPage = Math.max(pageCount, 1);
  const canPrevious = page > 1;
  const canNext = !isPageCountKnown || page < lastPage;
  const range = total != null && pageSize != null ? getPageRange(page, pageSize, total) : null;
  const pageSizeItems = pageSizeOptions.map((size) => ({
    label: String(size),
    value: String(size),
  }));

  return (
    <HStack gap={4} justify="between" wrap data-slot="pagination" className={cn('px-2 py-3', className)}>
      <div className="min-w-0 flex-1 text-muted-foreground text-xs">
        {summary ??
          (range && (
            <>
              {range.start}–{range.end} of {total}
            </>
          ))}
      </div>

      <HStack gap={4} justify="end" wrap>
        {onPageSizeChange && (
          <HStack gap={2}>
            <span className="whitespace-nowrap text-muted-foreground text-xs">Rows per page</span>
            <SimpleSelect
              items={pageSizeItems}
              value={String(pageSize)}
              onValueChange={(val) => val && onPageSizeChange(Number(val))}
              size="sm"
              className="w-16"
            />
          </HStack>
        )}

        <span className="whitespace-nowrap text-muted-foreground text-xs">
          Page {page}
          {isPageCountKnown && <> of {lastPage}</>}
        </span>

        <HStack gap={1}>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(1)}
            disabled={!canPrevious}
            aria-label="First page"
          >
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page - 1)}
            disabled={!canPrevious}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(page + 1)}
            disabled={!canNext}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={() => onPageChange(lastPage)}
            disabled={!isPageCountKnown || page >= lastPage}
            aria-label="Last page"
          >
            <ChevronsRight />
          </Button>
        </HStack>
      </HStack>
    </HStack>
  );
};

export type { PaginationProps };
export { Pagination };
